import { useState } from "react";
import { X, Calendar, Clock, User, Phone, Mail, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { useAuth } from "../App";

const InquiryModal = ({ isOpen, onClose, property, inquiryType = "callback" }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    preferred_date: "",
    preferred_time: "",
    message: "",
  });

  if (!isOpen) return null;

  const getTitle = () => {
    switch (inquiryType) {
      case "site_visit":
        return "Schedule Site Visit";
      case "video_tour":
        return "Book Video Tour";
      case "offer":
        return "Get Today's Offer";
      default:
        return "Request Callback";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/inquiries`, {
        property_id: property.property_id,
        property_name: property.name,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        inquiry_type: inquiryType,
        preferred_date: formData.preferred_date || null,
        preferred_time: formData.preferred_time || null,
        message: formData.message || null,
      });

      toast.success("Inquiry submitted! Our team will contact you soon.");
      onClose();
      setFormData({
        name: user?.name || "",
        email: user?.email || "",
        phone: "",
        preferred_date: "",
        preferred_time: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = [
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "2:00 PM",
    "3:00 PM",
    "4:00 PM",
    "5:00 PM",
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative bg-white w-full max-w-md shadow-float animate-scale-in"
        data-testid="inquiry-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-100">
          <h2 className="font-display text-xl text-stone-900">{getTitle()}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-full transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5 text-stone-500" strokeWidth={1.5} />
          </button>
        </div>

        {/* Property Info */}
        <div className="px-6 py-4 bg-stone-50 border-b border-stone-100">
          <p className="font-medium text-stone-900">{property.name}</p>
          <p className="text-sm text-stone-500">{property.locality}, {property.city}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1">
              <User className="w-3 h-3" strokeWidth={1.5} /> Full Name
            </Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter your name"
              className="input-document"
              required
              data-testid="inquiry-name-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1">
              <Mail className="w-3 h-3" strokeWidth={1.5} /> Email
            </Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Enter your email"
              className="input-document"
              required
              data-testid="inquiry-email-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1">
              <Phone className="w-3 h-3" strokeWidth={1.5} /> Phone Number
            </Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+91 97408 99729"
              className="input-document"
              required
              data-testid="inquiry-phone-input"
            />
          </div>

          {(inquiryType === "site_visit" || inquiryType === "video_tour") && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1">
                  <Calendar className="w-3 h-3" strokeWidth={1.5} /> Preferred Date
                </Label>
                <Input
                  type="date"
                  value={formData.preferred_date}
                  onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                  className="input-document"
                  min={new Date().toISOString().split("T")[0]}
                  data-testid="inquiry-date-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1">
                  <Clock className="w-3 h-3" strokeWidth={1.5} /> Time Slot
                </Label>
                <Select
                  value={formData.preferred_time}
                  onValueChange={(value) => setFormData({ ...formData, preferred_time: value })}
                >
                  <SelectTrigger className="input-document border-b-2 border-stone-200" data-testid="inquiry-time-select">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1">
              <MessageSquare className="w-3 h-3" strokeWidth={1.5} /> Message (Optional)
            </Label>
            <Textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Any specific requirements or questions?"
              className="input-document resize-none h-20"
              data-testid="inquiry-message-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full btn-primary"
            disabled={loading}
            data-testid="submit-inquiry-btn"
          >
            {loading ? "Submitting..." : "Submit Inquiry"}
          </Button>

          <p className="text-xs text-stone-400 text-center">
            By submitting, you agree to our terms and privacy policy.
          </p>
        </form>
      </div>
    </div>
  );
};

export default InquiryModal;
